<template>
	<section class="scene-content-index">
		<ChalkyUtilityTabGroup
			v-bind:tabs="['News', 'Videos', 'Tutorials']"
			v-bind:useUrl="true"
			v-on:tab-change="Handle_OnTabChange"
			v-slot="{ activeTab }"
		>
			<div v-if="activeTab === 'news'">
				<ViewContentList
					v-bind:contentCollection="newsCollection"
					v-on:content:select="Handle_OnSelectContent"
				/>
			</div>

			<div v-if="activeTab === 'videos'">
				<ViewContentList
					v-bind:contentCollection="videoCollection"
					v-on:content:select="Handle_OnSelectContent"
				/>
			</div>

			<div v-if="activeTab === 'tutorials'">
				<ViewContentList
					v-bind:contentCollection="tutorialCollection"
					v-on:content:select="Handle_OnSelectContent"
				/>
			</div>
		</ChalkyUtilityTabGroup>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Tabbed content browser scene with News, Videos, and Tutorials tabs.
	 * Manages tab state, triggers collection fetches on tab change,
	 * and emits content selection events to the parent Page.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/Content
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneContentIndex extends SceneBase {
		/**
		 * Collection of news content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop()
		public newsCollection!: ChalkySticks.Collection.Content;

		/**
		 * Collection of tutorial content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop()
		public tutorialCollection!: ChalkySticks.Collection.Content;

		/**
		 * Collection of video content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop()
		public videoCollection!: ChalkySticks.Collection.Content;

		/**
		 * Current active tab slug
		 *
		 * @type string
		 */
		protected currentActiveTab: string = '';

		/**
		 * Initialize scene and fetch data based on current tab.
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			this.currentActiveTab = this.$route.query.tab?.toString() || 'news';

			switch (this.currentActiveTab) {
				case 'videos':
					this.fetchVideos();
					break;

				case 'tutorials':
					this.fetchTutorials();
					break;

				case 'news':
				default:
					this.fetchNews();
					break;
			}
		}

		/**
		 * Fetch news content collection.
		 *
		 * @return void
		 */
		protected fetchNews(): void {
			if (Date.now() - this.newsCollection.timeCompleted < 1000 * 60) {
				return;
			}

			this.newsCollection.fetch();
		}

		/**
		 * Fetch tutorial content collection.
		 *
		 * @return void
		 */
		protected fetchTutorials(): void {
			if (Date.now() - this.tutorialCollection.timeCompleted < 1000 * 60) {
				return;
			}

			this.tutorialCollection.fetch();
		}

		/**
		 * Fetch video content collection.
		 *
		 * @return void
		 */
		protected fetchVideos(): void {
			if (Date.now() - this.videoCollection.timeCompleted < 1000 * 60) {
				return;
			}

			this.videoCollection.fetch();
		}

		/**
		 * Returns the active collection based on the current tab.
		 *
		 * @return ChalkySticks.Collection.Content
		 */
		protected getActiveCollection(): ChalkySticks.Collection.Content {
			switch (this.currentActiveTab) {
				case 'videos':
					return this.videoCollection;

				case 'tutorials':
					return this.tutorialCollection;

				case 'news':
				default:
					return this.newsCollection;
			}
		}

		/**
		 * Fetches next page of results for the active collection.
		 * Called by the parent Page via $refs when AutoScroll triggers.
		 *
		 * @return Promise<void>
		 */
		public async loadMore(): Promise<void> {
			try {
				const activeCollection = this.getActiveCollection();

				if (activeCollection.length > 0) {
					await activeCollection.nextPage(true);
				}
			} catch (error) {
				console.error('Error fetching more content:', error);
			}
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle content item selection. Emits to parent for navigation.
		 *
		 * @param ChalkySticks.Model.Content contentModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectContent(contentModel: ChalkySticks.Model.Content): Promise<void> {
			this.$emit('content:select', contentModel);
		}

		/**
		 * Handle tab change events. Fetches the appropriate collection.
		 *
		 * @param string slug
		 * @return Promise<void>
		 */
		protected async Handle_OnTabChange(slug: string): Promise<void> {
			this.currentActiveTab = slug;

			ChalkySticks.Utility.Analytics.track('content:tab.change', {
				tab: slug,
			});

			switch (slug) {
				case 'news':
					this.fetchNews();
					break;

				case 'videos':
					this.fetchVideos();
					break;

				case 'tutorials':
					this.fetchTutorials();
					break;
			}
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	// Layout
	// ---------------------------------------------------------------------------

	.scene-content-index {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}
</style>
